import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { pdfDocToBase64 } from '../utils/tripExport';
import { Mail, Loader2, CheckCircle, AlertTriangle, Send } from 'lucide-react';

// Reusable "Email PDF" action shared by the Vehicle Log Book, Person Log Book and
// Observed Beacon History modals. Gated by the same three email gates as Share
// Position (global feature flag + per-user privilege + General Settings override).
export const EmailReportModal = ({ open, onOpenChange, api, ctx }) => {
  const { isNotificationChannelActive, getNotificationDisabledReason } = useFeatureFlags();
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [alert, setAlert] = useState(null); // { success, message }

  const emailActive = isNotificationChannelActive('notification_email');
  const disabledReason = getNotificationDisabledReason('notification_email');
  const { reportLabel = 'Report', scope = '', count = 0, subject = 'EnsueLIVETRAK Report', buildPdf } = ctx || {};

  const handleClose = (v) => {
    if (!v) { setAlert(null); setRecipient(''); setMessage(''); }
    onOpenChange(v);
  };

  const handleSend = async () => {
    if (!recipient.trim()) { setAlert({ success: false, message: 'Please enter a recipient email address' }); return; }
    if (typeof buildPdf !== 'function') { setAlert({ success: false, message: 'Nothing to send' }); return; }
    setSending(true);
    setAlert(null);
    try {
      const { doc, filename } = await buildPdf();
      const pdf_base64 = pdfDocToBase64(doc);
      await api.post('/share/report-email', {
        recipient: recipient.trim(),
        subject,
        message: message.trim(),
        filename,
        pdf_base64,
        report: reportLabel,
        scope,
        count,
      });
      setAlert({ success: true, message: `Report emailed to ${recipient.trim()}` });
      setRecipient('');
      setMessage('');
    } catch (err) {
      setAlert({ success: false, message: err.response?.data?.detail || 'Failed to send email' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="email-report-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#2563eb]" /> Email {reportLabel} as PDF
          </DialogTitle>
          <DialogDescription>
            Forward this report straight from the log book as evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="p-3 rounded-lg bg-muted/50 border text-sm space-y-1">
          <p className="font-semibold" data-testid="email-report-scope">{reportLabel}{scope ? ` \u00b7 ${scope}` : ''}</p>
          <p className="text-xs text-muted-foreground">{count} record{count === 1 ? '' : 's'} will be attached as a branded PDF.</p>
        </div>

        {!emailActive ? (
          <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900 text-sm text-amber-800 dark:text-amber-300" data-testid="email-report-restricted">
            {disabledReason === 'user_setting'
              ? 'Email is switched off in your General Settings → Notifications. Turn on the Email channel to send reports.'
              : 'Email notifications are not enabled for your account. Contact your administrator to grant the Email privilege.'}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Recipient Email</Label>
              <Input type="email" value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="email@example.com" data-testid="email-report-recipient" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message (optional)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a note..." rows={2} className="text-sm" data-testid="email-report-message" />
            </div>
            <Button onClick={handleSend} disabled={sending} className="w-full" data-testid="email-report-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Email
            </Button>
          </div>
        )}

        {alert && (
          <div className={`p-3 rounded-lg border text-center ${alert.success ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`} data-testid="email-report-alert">
            <div className="flex items-center justify-center gap-2">
              {alert.success ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}
              <p className="text-sm font-medium">{alert.message}</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
